/**
 * 🔔 Logger Service
 * 
 * Unified logging service to replace random console.log calls.
 * Provides structured logging with levels, contexts, and environment awareness.
 * 
 * Usage:
 *   import { Logger } from '@/utils/logger';
 *   const log = Logger.create('MyComponent');
 *   log.info('Something happened');
 *   log.error('Error occurred', error);
 */

// Log levels (ordered by severity)
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  SILENT: 4
};

// Environment-based log level
const getDefaultLogLevel = () => {
  if (import.meta.env.PROD) {
    return LOG_LEVELS.WARN; // Production: only warnings and errors
  }
  if (import.meta.env.DEV) {
    return LOG_LEVELS.DEBUG; // Development: all logs
  }
  return LOG_LEVELS.INFO; // Default: info and above
};

// Global configuration
const config = {
  logLevel: getDefaultLogLevel(),
  enableTimestamp: true,
  enableContext: true,
  enableEmoji: true,
  persistErrors: false, // Future: send errors to monitoring service
  groupSimilar: true
};

// Emoji prefixes for different log types
const EMOJI = {
  DEBUG: '🔍',
  INFO: '📝',
  WARN: '⚠️',
  ERROR: '❌',
  SUCCESS: '✅',
  NETWORK: '🌐',
  DATABASE: '💾',
  AUTH: '🔐',
  TIMER: '⏱️',
  USER: '👤',
  SYSTEM: '⚙️'
};

// Format timestamp
const formatTimestamp = () => {
  const now = new Date();
  return `${now.toLocaleTimeString()}.${now.getMilliseconds().toString().padStart(3, '0')}`;
};

// Format log message
const formatMessage = (level, context, message, emoji = null) => {
  const parts = [];
  
  if (config.enableTimestamp) {
    parts.push(`[${formatTimestamp()}]`);
  }
  
  if (config.enableEmoji) {
    parts.push(emoji || EMOJI[level] || '');
  }
  
  if (config.enableContext && context) {
    parts.push(`[${context}]`);
  }
  
  parts.push(message);
  
  return parts.filter(Boolean).join(' ');
};

// Recent log cache for deduplication
const recentLogs = new Map();
const DEDUPE_WINDOW_MS = 1000;

const shouldLog = (level, message) => {
  if (LOG_LEVELS[level] < config.logLevel) {
    return false;
  }
  
  // Deduplicate similar logs within time window
  if (config.groupSimilar) {
    const key = `${level}:${message}`;
    const now = Date.now();
    const lastLogged = recentLogs.get(key);
    
    if (lastLogged && (now - lastLogged) < DEDUPE_WINDOW_MS) {
      return false;
    }
    
    recentLogs.set(key, now);
    
    // Clean old entries periodically
    if (recentLogs.size > 100) {
      for (const [k, v] of recentLogs) {
        if (now - v > DEDUPE_WINDOW_MS * 2) {
          recentLogs.delete(k);
        }
      }
    }
  }
  
  return true;
};

// Create a logger instance with a specific context
const createLogger = (context) => {
  return {
    /**
     * Debug level - detailed information for debugging
     */
    debug(message, ...args) {
      if (shouldLog('DEBUG', message)) {
        console.log(formatMessage('DEBUG', context, message), ...args);
      }
    },
    
    /**
     * Info level - general informational messages
     */
    info(message, ...args) {
      if (shouldLog('INFO', message)) {
        console.log(formatMessage('INFO', context, message), ...args);
      }
    },
    
    /**
     * Warning level - potential issues that don't stop execution
     */
    warn(message, ...args) {
      if (shouldLog('WARN', message)) {
        console.warn(formatMessage('WARN', context, message), ...args);
      }
    },
    
    /**
     * Error level - errors that may affect functionality
     */
    error(message, error = null, ...args) {
      if (shouldLog('ERROR', message)) {
        const formattedMessage = formatMessage('ERROR', context, message);
        
        if (error instanceof Error) {
          console.error(formattedMessage, error.message, ...args);
          if (error.stack && import.meta.env.DEV) {
            console.error(error.stack);
          }
        } else if (error) {
          console.error(formattedMessage, error, ...args);
        } else {
          console.error(formattedMessage, ...args);
        }
        
        // Future: Send to error monitoring service
        if (config.persistErrors) {
          // errorMonitoringService.capture(error, { context, message });
        }
      }
    },
    
    /**
     * Success message - positive outcomes
     */
    success(message, ...args) {
      if (shouldLog('INFO', message)) {
        console.log(formatMessage('INFO', context, message, EMOJI.SUCCESS), ...args);
      }
    },
    
    /**
     * Network-related logs
     */
    network(message, ...args) {
      if (shouldLog('DEBUG', message)) {
        console.log(formatMessage('DEBUG', context, message, EMOJI.NETWORK), ...args);
      }
    },
    
    /**
     * Database-related logs
     */
    database(message, ...args) {
      if (shouldLog('DEBUG', message)) {
        console.log(formatMessage('DEBUG', context, message, EMOJI.DATABASE), ...args);
      }
    },
    
    /**
     * Auth-related logs
     */
    auth(message, ...args) {
      if (shouldLog('INFO', message)) {
        console.log(formatMessage('INFO', context, message, EMOJI.AUTH), ...args);
      }
    },
    
    /**
     * Group related logs
     */
    group(label, fn) {
      if (config.logLevel <= LOG_LEVELS.DEBUG) {
        console.group(formatMessage('DEBUG', context, label));
        try {
          fn();
        } finally {
          console.groupEnd();
        }
      } else {
        fn();
      }
    },
    
    /**
     * Time measurement
     */
    time(label) {
      const start = performance.now();
      return {
        end: (message = null) => {
          const duration = (performance.now() - start).toFixed(2);
          if (shouldLog('DEBUG', label)) {
            console.log(formatMessage('DEBUG', context, message || `${label}: ${duration}ms`, EMOJI.TIMER));
          }
          return parseFloat(duration);
        }
      };
    },
    
    /**
     * Table output
     */
    table(data, columns = null) {
      if (config.logLevel <= LOG_LEVELS.DEBUG) {
        console.log(formatMessage('DEBUG', context, 'Table data:'));
        console.table(data, columns);
      }
    }
  };
};

// Main Logger export
export const Logger = {
  /**
   * Create a new logger instance with context
   * @param {string} context - The context/component name
   * @returns {Object} Logger instance
   */
  create(context) {
    return createLogger(context);
  },
  
  /**
   * Configure global logging settings
   * @param {Object} options - Configuration options
   */
  configure(options) {
    Object.assign(config, options);
  },
  
  /**
   * Set the minimum log level
   * @param {string} level - 'DEBUG', 'INFO', 'WARN', 'ERROR', or 'SILENT'
   */
  setLevel(level) {
    if (LOG_LEVELS[level] !== undefined) {
      config.logLevel = LOG_LEVELS[level];
    }
  },
  
  /**
   * Enable all logging (DEBUG level)
   */
  enableAll() {
    config.logLevel = LOG_LEVELS.DEBUG;
  },
  
  /**
   * Disable all logging (SILENT)
   */
  disableAll() {
    config.logLevel = LOG_LEVELS.SILENT;
  },
  
  /**
   * Available log levels
   */
  LEVELS: LOG_LEVELS,
  
  /**
   * Available emoji prefixes
   */
  EMOJI
};

// Default logger instance (no context)
export const log = createLogger(null);

// Convenience exports for quick usage
export const debug = (message, ...args) => log.debug(message, ...args);
export const info = (message, ...args) => log.info(message, ...args);
export const warn = (message, ...args) => log.warn(message, ...args);
export const error = (message, error, ...args) => log.error(message, error, ...args);

export default Logger;
